// frontend/src/components/ui/date-range-picker.tsx
"use client"

import * as React from "react"
import { format } from "date-fns"
import { Calendar as CalendarIcon } from "lucide-react"
import { type DateRange } from "react-day-picker"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

interface DatePickerWithRangeProps extends React.HTMLAttributes<HTMLDivElement> {
  date?: DateRange;
  onDateChange?: (date: DateRange | undefined) => void;
  className?: string;
  disabled?: boolean;
  numberOfMonths?: 1 | 2;
  placeholder?: string;
}

export function DatePickerWithRange({
  className,
  date: initialDate,
  onDateChange,
  disabled = false,
  numberOfMonths = 2,
  placeholder = "Pick a date range",
}: DatePickerWithRangeProps) {
  const [date, setDate] = React.useState<DateRange | undefined>(initialDate);
  const [isOpen, setIsOpen] = React.useState(false);

  React.useEffect(() => {
    setDate(initialDate);
  }, [initialDate]);

  const handleSelect = (selectedRange: DateRange | undefined) => {
    setDate(selectedRange);
    if (onDateChange) {
      onDateChange(selectedRange);
    }
    if (selectedRange?.from && selectedRange?.to) {
      setIsOpen(false); // Close popover when a full range is selected
    }
  };

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          {/* Removed explicit onClick from Button. PopoverTrigger + onOpenChange on Popover handle this. */}
          <Button
            id="date"
            variant={"outline"}
            className={cn(
              "w-full justify-start text-left font-normal border-border focus:border-primary focus:ring-primary !bg-input text-foreground placeholder:text-muted-foreground/80",
              !date && "text-muted-foreground"
            )}
            disabled={disabled}
          >
            <CalendarIcon className="mr-2 h-4 w-4 text-muted-foreground" />
            {date?.from ? (
              date.to ? (
                <>
                  {format(date.from, "LLL dd, y")} -{" "}
                  {format(date.to, "LLL dd, y")}
                </>
              ) : (
                format(date.from, "LLL dd, y")
              )
            ) : (
              <span>{placeholder}</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent 
          className="!absolute w-auto p-0 !bg-popover !border-border z-[100]" // Added z-[120] like your working example
          sideOffset={5} 
        >
          <Calendar
            initialFocus
            mode="range"
            defaultMonth={date?.from}
            selected={date}
            onSelect={handleSelect}
            numberOfMonths={numberOfMonths}
            disabled={disabled}
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}